/**
 * The assistant's light markdown, parsed into plain data: paragraphs, bullet
 * and numbered lists, a heading line drawn as bold, `**bold**`, and links.
 *
 * Never HTML. The renderer builds React elements from these nodes, so nothing
 * the model writes is ever parsed by the browser as markup. Links are checked
 * here: an external one must clear `safeExternalUrl` (http/https), an internal
 * one must be on the assistant's route allow-list. Anything else stays text.
 */

import { safeExternalUrl } from '../../lib/url';
import { allowedAssistantRoute } from './assistantContext';

export type InlineNode =
  | { kind: 'text'; text: string }
  | { kind: 'bold'; children: InlineNode[] }
  | { kind: 'link'; text: string; href: string; internal: boolean };

export type BlockNode =
  | { kind: 'paragraph'; lines: InlineNode[][] }
  | { kind: 'heading'; content: InlineNode[] }
  | { kind: 'list'; ordered: boolean; items: InlineNode[][] };

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*\d{1,3}[.)]\s+(.*)$/;
const HEADING = /^\s{0,3}#{1,6}\s+(.*)$/;
const CONTINUATION = /^\s{2,}\S/;

/** `**bold**`, `[text](target)`, or a bare http(s) address. */
const INLINE = /\*\*(.+?)\*\*|\[([^\]\n]+)\]\(([^)\s]+)\)|(https?:\/\/[^\s<>()]+)/g;

/** Sentence punctuation that trails a bare URL is not part of it. */
const TRAILING = /[.,;:!?'"]+$/;

function linkNode(text: string, target: string): InlineNode {
  if (target.startsWith('/')) {
    const route = allowedAssistantRoute(target);
    if (route !== null) return { kind: 'link', text, href: route, internal: true };
    return { kind: 'text', text };
  }
  const href = safeExternalUrl(target);
  return href === null ? { kind: 'text', text } : { kind: 'link', text, href, internal: false };
}

export function parseInline(source: string, allowBold = true): InlineNode[] {
  const nodes: InlineNode[] = [];
  let last = 0;

  function pushText(text: string) {
    if (!text) return;
    const previous = nodes[nodes.length - 1];
    if (previous?.kind === 'text') previous.text += text;
    else nodes.push({ kind: 'text', text });
  }

  for (const match of source.matchAll(INLINE)) {
    const index = match.index;
    const [whole, bold, linkText, linkTarget, bare] = match;

    if (bold !== undefined) {
      if (!allowBold) continue;
      pushText(source.slice(last, index));
      nodes.push({ kind: 'bold', children: parseInline(bold, false) });
    } else if (linkText !== undefined && linkTarget !== undefined) {
      pushText(source.slice(last, index));
      nodes.push(linkNode(linkText, linkTarget));
    } else if (bare !== undefined) {
      const trailing = TRAILING.exec(bare)?.[0] ?? '';
      const url = bare.slice(0, bare.length - trailing.length);
      pushText(source.slice(last, index));
      nodes.push(linkNode(url, url));
      pushText(trailing);
    } else {
      continue;
    }
    last = index + whole.length;
  }

  pushText(source.slice(last));
  return nodes;
}

/** The whole message as blocks. Total: any string parses. */
export function parseLightMarkdown(source: string): BlockNode[] {
  const blocks: BlockNode[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  function flushParagraph() {
    if (paragraph.length > 0) {
      blocks.push({ kind: 'paragraph', lines: paragraph.map((line) => parseInline(line)) });
      paragraph = [];
    }
  }

  function flushList() {
    if (list !== null) {
      blocks.push({
        kind: 'list',
        ordered: list.ordered,
        items: list.items.map((item) => parseInline(item)),
      });
      list = null;
    }
  }

  for (const rawLine of source.replace(/\r\n?/g, '\n').split('\n')) {
    const line = rawLine.trimEnd();

    if (!line.trim()) {
      flushParagraph();
      flushList();
      continue;
    }

    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    const item = bullet ?? numbered;
    if (item) {
      flushParagraph();
      const ordered = numbered !== null;
      if (list !== null && list.ordered !== ordered) flushList();
      list ??= { ordered, items: [] };
      list.items.push(item[1] ?? '');
      continue;
    }

    if (list !== null && CONTINUATION.test(rawLine)) {
      const items = list.items;
      items[items.length - 1] = `${items[items.length - 1] ?? ''} ${line.trim()}`;
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({ kind: 'heading', content: parseInline(heading[1] ?? '') });
      continue;
    }

    flushList();
    paragraph.push(line);
  }

  flushParagraph();
  flushList();
  return blocks;
}
